import { isReplayingAtom, } from '@/lib/store';
import { artifactAtom } from '@/lib/store/artifact';
import { setCurrentTabLoadingAtom, sidebarLayoutAtom, SidebarStatus } from '@/lib/store/sidebar';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTheme } from 'next-themes';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';

const Editor = lazy(async () => {
  return await import('@monaco-editor/react').then(mod => ({ default: mod.Editor }))
});

export const ArtifactsEditor = () => {
  const isReplaying = useAtomValue(isReplayingAtom);
  const editorRef = useRef<any>(null);
  const { code: realCode } = useAtomValue(artifactAtom);
  const { sidebarStatus } = useAtomValue(sidebarLayoutAtom);
  const [code, setCode] = useState('');
  const setLoading = useSetAtom(setCurrentTabLoadingAtom);

  const { systemTheme } = useTheme()

  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor;

    try {
      const commandService = editor._commandService;
      if (commandService) {
        const noop = () => { };

        const commonCommands = [
          'actions.find', 'editor.action.startFindReplaceAction',
          'editor.action.clipboardCutAction', 'editor.action.clipboardCopyAction',
          'editor.action.formatDocument', 'editor.action.commentLine',
          'editor.action.quickCommand', 'editor.action.showContextMenu',
          'editor.action.toggleStickyScroll', 'editor.action.showStickyScroll'
        ];

        commonCommands.forEach(commandId => {
          try {
            if (commandService._commands.has(commandId)) {
              const originalCommand = commandService._commands.get(commandId);
              if (originalCommand) {
                const disabledCommand = {
                  ...originalCommand,
                  runCommand: noop
                };
                commandService._commands.set(commandId, disabledCommand);
              }
            }
          } catch (e) {
            console.debug('Failed to disable command:', commandId);
          }
        });
      }

      if (editor._configurationService) {
        try {
          editor._configurationService.updateValue('editor.stickyScroll.enabled', false);
          editor._configurationService.updateValue('editor.stickyScroll.maxLineCount', 0);
        } catch (e) {
          console.debug('Failed to disable sticky scroll:', e);
        }
      }
    } catch (e) {
      console.debug('Failed to disable editor commands:', e);
    }
    try {
      if (monaco.languages.typescript?.javascriptDefaults) {
        monaco.languages.typescript.javascriptDefaults.setCompilerOptions({ noLib: true });
      }

      if (monaco.languages.css?.cssDefaults) {
        monaco.languages.css.cssDefaults.setOptions({ validate: false });
      }

      if (monaco.languages.html?.htmlDefaults) {
        monaco.languages.html.htmlDefaults.setOptions({
          validate: false,
          format: {
            contentUnformatted: 'pre,code,textarea',
            extraLiners: '',
            indentInnerHtml: false,
            maxPreserveNewLines: 1,
            preserveNewLines: true,
            wrapAttributes: 'auto',
            wrapLineLength: 120
          },
          suggest: { html5: false }
        });
      }


      if (monaco.languages.html) {
        if (typeof monaco.languages.html.disableStickyScroll === 'function') {
          monaco.languages.html.disableStickyScroll();
        }

        const htmlConf = monaco.languages.html.conf;
        if (htmlConf) {
          const simplifiedConf = { ...htmlConf };
          simplifiedConf.foldingStrategy = 'disabled';
          monaco.languages.setLanguageConfiguration('html', simplifiedConf);
        }
      }
    } catch (e) {
      console.debug('Failed to disable language services:', e);
    }
  }

  useEffect(() => {
    if (sidebarStatus === SidebarStatus.Close) {
      return;
    }

    if (isReplaying) {
      return;
    }

    setLoading(true);
    setCode('');
    const timeout = setTimeout(() => {
      setLoading(false);
      setCode(realCode);
    }, 300);
    return () => clearTimeout(timeout);
  }, [realCode, isReplaying, SidebarStatus])

  useEffect(() => {
    if (isReplaying && editorRef.current) {
      setTimeout(() => {
        const lineCount = editorRef.current.getModel()?.getLineCount() || 0;
        if (lineCount > 0) {
          editorRef.current.revealLine(lineCount);
        }
      }, 100);
    }
  }, [isReplaying, realCode]);

  return <Suspense fallback={<div>Loading Editor...</div>}>
    <div className='p-[20px] h-full bg-transparent'>
      <Editor
        height="100%"
        width="100%"
        language='html'
        theme={systemTheme === 'dark' ? 'vs-dark' : 'light'}
        value={isReplaying ? realCode : code}
        onMount={handleEditorDidMount}
        options={{
          readOnly: true,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          lineNumbers: "off",
          glyphMargin: false,
          folding: false,
          lineDecorationsWidth: 0,
          overviewRulerLanes: 0,
          renderLineHighlight: "none",
          contextmenu: false,
          quickSuggestions: false,
          suggestOnTriggerCharacters: false,
          parameterHints: { enabled: false },
          wordWrap: "on",
          scrollbar: {
            vertical: 'visible',
            horizontal: 'visible',
            useShadows: false,
            verticalHasArrows: false,
            horizontalHasArrows: false,
            alwaysConsumeMouseWheel: false
          },
          find: {
            addExtraSpaceOnTop: false,
            autoFindInSelection: 'never',
            seedSearchStringFromSelection: 'never'
          },
          links: false,
          hover: { enabled: false },
          colorDecorators: false,
          occurrencesHighlight: "off",
          accessibilitySupport: 'off',
          formatOnType: false,
          formatOnPaste: false,
          automaticLayout: true,
          codeLens: false,
          wordBasedSuggestions: "off",
          tabCompletion: 'off',
          renderControlCharacters: false,
          hideCursorInOverviewRuler: true,
          matchBrackets: 'never',
          selectionHighlight: false,
          cursorStyle: 'line-thin',
          cursorBlinking: 'solid',
          renderWhitespace: 'none',
          rulers: [],
          renderFinalNewline: "off",
          guides: { indentation: false },
          cursorSmoothCaretAnimation: 'off',
          fontLigatures: false,
          disableMonospaceOptimizations: true,
          unfoldOnClickAfterEndOfLine: false,
          dragAndDrop: false,
          acceptSuggestionOnCommitCharacter: false,
          acceptSuggestionOnEnter: 'off',
          ariaLabel: 'HTML Preview',
          domReadOnly: true,
          stickyScroll: { enabled: false },
        }}
      />
    </div>
  </Suspense>
}
